<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import FilterDrawerCheckboxList from '.././FilterDrawerCheckboxList/FilterDrawerCheckboxList.svelte';
	import FilterDrawerRangeSlider from '.././FilterDrawerRangeSlider/FilterDrawerRangeSlider.svelte';
	import FilterDrawerTags from '.././FilterDrawerTags/FilterDrawerTags.svelte';
	import { FilterDrawerToggle } from '.././FilterDrawerToggle';
	import { ScrollHintOverlays } from '../../../../../components/ScrollHintOverlays';
	import { Txt } from '../../../../../components/Txt';
	import {
		initialVerticalScrollState,
		verticalScrollHint,
		type VerticalScrollState
	} from '../../../../../utils/verticalScrollHint/verticalScrollHint';
	import { FilterCollection } from '../../../constants';
	import type {
		FilterCheckboxList,
		FilterSectionFilterType,
		FilterSlider,
		FilterTagList,
		FilterToggle,
		FilterSection
	} from '../../../types';

	interface Props {
		filterSection: FilterSection;
		showBottomBorder: boolean;
		withMaxSectionHeight: boolean;
		/** Clamp the parent flex item (the accordion's content element) instead of
		 * this component's own wrapper — for when the wrapper is nested inside the
		 * element that shares the drawer's flex column. */
		clampParentFlexItem?: boolean;
		class?: string;
	}

	let { ...props }: Props = $props();

	// Drives the section's own scroll-hint gradients so it's clear more options
	// exist below the fold of a height-constrained option list.
	let listScrollState: VerticalScrollState = $state(initialVerticalScrollState);

	let wrapperElement: HTMLElement | undefined = $state();
	let naturalContentHeight = $state(0);

	// Cap the section's flex item at its content via a measured pixel max-height.
	// CSS `max-height: max-content` would express this, but WebKit resolves it
	// against the item's zero flex basis instead of its content, collapsing every
	// section to nothing on iOS.
	$effect(() => {
		const flexItem = props.clampParentFlexItem ? wrapperElement?.parentElement : wrapperElement;

		if (flexItem && naturalContentHeight > 0) {
			flexItem.style.maxHeight = `${naturalContentHeight}px`;
		}
	});

	const getFilterTagList = (filterType: FilterSectionFilterType) =>
		filterType?.item as FilterTagList;

	const getFilterSlider = (filterType: FilterSectionFilterType) => filterType?.item as FilterSlider;

	const getFilterCheckboxList = (filterType: FilterSectionFilterType) =>
		filterType?.item as FilterCheckboxList;

	const getFilterToggle = (filterType: FilterSectionFilterType) => filterType?.item as FilterToggle;

	const shouldShowLabelAbove = (filterType: FilterSectionFilterType) => {
		const hasMultipleFilterTypes = (props.filterSection?.filter_type?.length || 0) > 1;

		switch (filterType?.collection) {
			case FilterCollection.Filter_Tag_List:
			case FilterCollection.Filter_Checkbox_List:
				return hasMultipleFilterTypes;
			default:
				return false;
		}
	};

	const getFilterTypeLabel = (filterType: FilterSectionFilterType) =>
		(filterType.item as { label: string }).label;

	const listClass = $derived(
		twMerge(
			classNames('flex flex-col gap-6 overflow-y-auto bg-gray-100 px-4 py-3 md:px-6', {
				'border-b border-gray-200': props.showBottomBorder,
				// Shrink (and scroll) to whatever height the drawer's flex column
				// allots this section — but never stretch beyond the content: the
				// list's scrollHeight is what the section's max-height clamp is
				// measured from, and a stretched box would inflate it permanently.
				'min-h-0': props.withMaxSectionHeight
			}),
			props.class
		)
	);
</script>

{#snippet optionList()}
	{#if props.filterSection?.filter_type}
		{#each props.filterSection?.filter_type as filterType}
			<div>
				{#if filterType && shouldShowLabelAbove(filterType)}
					<Txt variant="label3" class="mb-4">{getFilterTypeLabel(filterType)}</Txt>
				{/if}

				{#if filterType?.collection === FilterCollection.Filter_Tag_List}
					<FilterDrawerTags
						filterCollectionId={filterType?.id}
						filterTagList={getFilterTagList(filterType)}
					/>
				{/if}

				{#if filterType?.collection === FilterCollection.Filter_Slider}
					<FilterDrawerRangeSlider
						filterCollectionId={filterType?.id}
						filterSlider={getFilterSlider(filterType)}
					/>
				{/if}

				{#if filterType?.collection === FilterCollection.Filter_Checkbox_List}
					<FilterDrawerCheckboxList
						filterCollectionId={filterType?.id}
						filterCheckboxList={getFilterCheckboxList(filterType)}
					/>
				{/if}

				{#if filterType?.collection === FilterCollection.Filter_Toggle}
					<FilterDrawerToggle
						filterCollectionId={filterType?.id}
						filterToggle={getFilterToggle(filterType)}
					/>
				{/if}
			</div>
		{/each}
	{/if}
{/snippet}

{#if props.withMaxSectionHeight}
	<!-- `overflow-hidden` clips the list's incompressible padding when the section
	     is squeezed below it, so it can't paint over neighbouring sections. -->
	<div bind:this={wrapperElement} class="relative flex min-h-0 grow flex-col overflow-hidden">
		<div
			use:verticalScrollHint={{
				onChange: (state) => {
					listScrollState = state;
				},
				onContentHeightChange: (height) => {
					naturalContentHeight = height;
				}
			}}
			class={listClass}
		>
			{@render optionList()}
		</div>

		<ScrollHintOverlays dataCy="filter-type-body" scrollState={listScrollState} />
	</div>
{:else}
	<div class={listClass}>
		{@render optionList()}
	</div>
{/if}
